// A draggable number card. Drawn entirely with vector Graphics so it stays
// crisp at any resolution. Pivot is centered so scale/rotate feel natural.
import { Container, Graphics, Text } from "pixi.js";
import { palette, FONT } from "./theme";
import { Ease, cancelMotion, fadeTo, scaleTo, tween } from "./juice";

export class Card extends Container {
  readonly value: number;
  readonly cardW: number;
  readonly cardH: number;

  // resting pose in the hand, restored after a drag
  homeX = 0;
  homeY = 0;
  homeRot = 0;

  dragging = false;
  locked = false;

  private glow: Graphics;

  constructor(value: number, w: number, h: number) {
    super();
    this.value = value;
    this.cardW = w;
    this.cardH = h;
    this.pivot.set(w / 2, h / 2);

    // soft glow behind the card (alpha animated on hover/pickup)
    this.glow = new Graphics();
    this.glow.roundRect(-10, -10, w + 20, h + 20, 24).fill(palette.glow);
    this.glow.alpha = 0;
    this.addChild(this.glow);

    // face
    const face = new Graphics();
    face
      .roundRect(0, 0, w, h, 16)
      .fill(palette.cardFace)
      .stroke({ width: 3, color: palette.cardEdge, alignment: 1 });
    face.roundRect(5, 5, w - 10, h - 10, 12).stroke({ width: 1.5, color: 0x000000, alpha: 0.08 });
    this.addChild(face);

    // top gloss highlight
    const gloss = new Graphics();
    gloss.roundRect(w * 0.1, h * 0.07, w * 0.8, h * 0.26, 12).fill({ color: 0xffffff, alpha: 0.22 });
    this.addChild(gloss);

    // big central numeral
    const label = new Text({
      text: String(value),
      style: { fontFamily: FONT, fontWeight: "700", fontSize: Math.floor(h * 0.42), fill: palette.ink },
    });
    label.anchor.set(0.5);
    label.position.set(w / 2, h / 2);
    this.addChild(label);

    // corner pips like a playing card
    for (const corner of [0, 1] as const) {
      const pip = new Text({
        text: String(value),
        style: { fontFamily: FONT, fontWeight: "600", fontSize: Math.floor(h * 0.12), fill: palette.ink },
      });
      pip.anchor.set(0.5);
      pip.alpha = 0.5;
      if (corner === 0) pip.position.set(w * 0.17, h * 0.14);
      else {
        pip.position.set(w * 0.83, h * 0.86);
        pip.rotation = Math.PI;
      }
      this.addChild(pip);
    }

    this.eventMode = "static";
    this.cursor = "pointer";
  }

  setGlow(target: number, duration = 150) {
    fadeTo(this.glow, target, duration);
  }

  hoverIn() {
    if (this.dragging || this.locked) return;
    scaleTo(this, 1.1, 150, Ease.outBack);
    this.setGlow(0.4);
  }

  hoverOut() {
    if (this.dragging || this.locked) return;
    scaleTo(this, 1, 170, Ease.outCubic);
    this.setGlow(0);
  }

  /** the "lift" when a card is grabbed */
  pop() {
    scaleTo(this, 1.16, 110, Ease.outBack);
    this.setGlow(0.85);
  }

  /** sweep a non-chosen card off the table, then destroy it */
  flyOut() {
    this.eventMode = "none";
    this.locked = true;
    cancelMotion(this); // this tween ends in destroy() — no other tween may outlive it
    const sx = this.x;
    const sy = this.y;
    const tx = sx + (Math.random() * 2 - 1) * 90;
    const ty = sy + 180;
    tween({
      duration: 340,
      ease: Ease.inOutCubic,
      owner: this,
      onUpdate: (v) => {
        this.x = sx + (tx - sx) * v;
        this.y = sy + (ty - sy) * v;
        this.alpha = 1 - v;
        this.scale.set(1 - 0.3 * v);
        this.rotation = (tx - sx) * 0.0025 * v;
      },
      onComplete: () => {
        if (!this.destroyed) this.destroy();
      },
    });
  }
}
